<script setup>
import { useReferenceWorkspace } from "./referenceWorkspace";
const props = defineProps({ responsive: Boolean });
const { canViewPayrollClaims, payrollClaimRows, setSectionRef } =
  useReferenceWorkspace();
</script>

<template>
  <section
    v-if="canViewPayrollClaims"
    id="payroll"
    :ref="(el) => setSectionRef('payroll', el)"
    class="min-w-0 scroll-mt-24 2xl:col-span-12"
  >
    <div class="mb-3">
      <div
        class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
      >
        Payroll
      </div>
      <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
        Claimed Hours
      </h2>
    </div>

    <FormBox
      header="Payroll Claims"
      class="payroll-claims"
      :class="{ 'payroll-responsive': props.responsive }"
    >
      <div
        v-if="payrollClaimRows.length === 0"
        class="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-300"
      >
        No payroll claims recorded for this reference.
      </div>
      <template v-else>
        <div v-if="props.responsive" class="payroll-cards space-y-3">
          <article
            v-for="claim in payrollClaimRows"
            :key="claim.id"
            class="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
          >
            <div class="flex flex-wrap justify-between gap-2 font-semibold">
              <span>{{ claim.staffName }}</span
              ><span>{{ claim.hours }} hours</span>
            </div>
            <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {{ claim.date || "Date unavailable" }} ·
              {{ claim.product || "Unmapped" }}
            </p>
            <details class="mt-3 text-sm">
              <summary
                class="cursor-pointer py-2 font-semibold text-sky-700 dark:text-sky-300"
              >
                Claim details
              </summary>
              <dl
                class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 break-words"
              >
                <dt>Rate</dt>
                <dd>{{ claim.rateCode || "n/a" }}</dd>
                <dt>Coworker</dt>
                <dd>{{ claim.coWorker || "Unmapped" }}</dd>
                <dt>Callsign</dt>
                <dd>{{ claim.callSign || "n/a" }}</dd>
                <dt>Source</dt>
                <dd>{{ claim.source || "n/a" }}</dd>
                <dt>Notes</dt>
                <dd class="whitespace-pre-line">
                  {{ claim.notes || "No notes" }}
                </dd>
              </dl>
            </details>
          </article>
        </div>
        <div
          class="payroll-table overflow-x-auto"
          tabindex="0"
          role="region"
          aria-label="Payroll claims table"
        >
          <table class="w-full text-sm">
            <thead>
              <tr
                class="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-700"
              >
                <th class="p-3">Date</th>
                <th class="p-3">Staff</th>
                <th class="p-3">Rate</th>
                <th class="p-3">Product</th>
                <th class="p-3">Hours</th>
                <th class="p-3">Coworker</th>
                <th class="p-3">Callsign</th>
                <th class="p-3">Source</th>
                <th class="p-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="claim in payrollClaimRows"
                :key="claim.id"
                class="border-b border-slate-100 transition-colors hover:bg-gray-100 dark:border-slate-800 dark:hover:bg-gray-900"
              >
                <td class="p-3 whitespace-nowrap">
                  {{ claim.date || "Date unavailable" }}
                </td>
                <td class="p-3 font-semibold">{{ claim.staffName }}</td>
                <td class="p-3">{{ claim.rateCode || "n/a" }}</td>
                <td class="p-3">{{ claim.product || "Unmapped" }}</td>
                <td class="p-3">{{ claim.hours }}</td>
                <td class="p-3">{{ claim.coWorker || "Unmapped" }}</td>
                <td class="p-3">{{ claim.callSign || "n/a" }}</td>
                <td class="p-3 whitespace-nowrap text-xs text-slate-500">
                  {{ claim.source }}
                </td>
                <td class="p-3 min-w-64">{{ claim.notes }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </FormBox>
  </section>
</template>

<style scoped>
.payroll-claims {
  container: payroll / inline-size;
}
.payroll-responsive .payroll-table {
  display: none;
}
@container payroll (min-width: 48rem) {
  .payroll-responsive .payroll-cards {
    display: none;
  }
  .payroll-responsive .payroll-table {
    display: block;
  }
}
</style>
